import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, X, Tag, Search, Download, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { submitPrintJob } from "@/lib/printJobs";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const ShelfTalkerModal = ({ open, onClose, items = [] }) => {
  const [companyInfo, setCompanyInfo] = useState({ logo: "", name: "", shortName: "BBH", offsetX: 0, offsetY: 0 });
  const [printing, setPrinting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [prevOpen, setPrevOpen] = useState(false);

  // Initialize selected items from props (items that have print_shelf_talker not set to false)
  const getInitialSelectedItems = () => items.filter(item => item.print_shelf_talker !== false).map(item => item.product_id);
  const [selectedItems, setSelectedItems] = useState(getInitialSelectedItems);

  // Fetch company settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/company`);
        setCompanyInfo({
          logo: response.data.logo_url || "",
          name: response.data.company_name || "BOSVELD BOU & HARDEWARE",
          shortName: response.data.short_name || "BBH",
          offsetX: Number.isFinite(response.data.sticker_offset_x_mm) ? Number(response.data.sticker_offset_x_mm) : 0,
          offsetY: Number.isFinite(response.data.sticker_offset_y_mm) ? Number(response.data.sticker_offset_y_mm) : 0,
        });
      } catch (error) {
        console.error("Error fetching company settings:", error);
      }
    };
    fetchSettings();
  }, []);

  // After a GRV commits, the parent passes items with the LOCAL (form-time)
  // selling_price / new_selling_price values, which can be stale relative to
  // what the backend actually wrote to the product doc. On modal open we
  // re-fetch the fresh selling_price_incl_vat from `/api/products/{id}` per
  // item and merge it in, so the printed label ALWAYS matches Product Master.
  const [freshPriceById, setFreshPriceById] = useState({});
  useEffect(() => {
    if (!open || !items || items.length === 0) return;
    let cancelled = false;
    const refresh = async () => {
      const ids = [...new Set(items.map(it => it.product_id).filter(Boolean))];
      const results = await Promise.all(ids.map(async (id) => {
        try {
          const r = await axios.get(`${API}/products/${id}`);
          return [id, Number(r.data?.selling_price_incl_vat) || 0];
        } catch (_err) {
          return [id, null];
        }
      }));
      if (cancelled) return;
      const map = {};
      for (const [id, price] of results) {
        if (price && price > 0) map[id] = price;
      }
      setFreshPriceById(map);
    };
    refresh();
    return () => { cancelled = true; };
  }, [open, items]);

  // Resolve the label price for one item: prefer the freshly-fetched DB
  // price, then fall back to whatever the parent passed us. This keeps the
  // frontend/backend priority order consistent (label PDF endpoint agrees).
  const priceFor = (item) => {
    const fresh = freshPriceById[item.product_id];
    if (fresh && fresh > 0) return fresh;
    return Number(item.new_selling_price) || Number(item.selling_price) || 0;
  };

  // Reset selection when modal opens (using previous state pattern)
  if (open && !prevOpen) {
    setSelectedItems(getInitialSelectedItems());
    setSearchQuery("");
    setPrevOpen(true);
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  const filteredItems = items.filter(item => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name?.toLowerCase().includes(query) ||
      item.plu_code?.toLowerCase().includes(query) ||
      item.barcode?.toLowerCase().includes(query)
    );
  });

  const toggleItem = (productId) => {
    setSelectedItems(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleAll = () => {
    const filteredIds = filteredItems.map(item => item.product_id);
    const allFilteredSelected = filteredIds.every(id => selectedItems.includes(id));
    
    if (allFilteredSelected) {
      setSelectedItems(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedItems(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const buildShelfTalkerHtml = () => {
    const selectedData = items.filter(item => selectedItems.includes(item.product_id));
    if (selectedData.length === 0) return null;
    let labelsHtml = '';
    selectedData.forEach(item => {
      const shelfPart = item.location?.split('/')[0] || item.shelf_number || '-';
      const binPart = item.location?.split('/')[1] || item.bin_number || '-';
      const price = priceFor(item);
      labelsHtml += `
        <div class="bh-label">
          <div class="bh-row1">
            <div class="bh-logo">${companyInfo.shortName || 'BBH'}</div>
            <div class="bh-price">R${price.toFixed(2)}</div>
          </div>
          <div class="bh-row2">
            <span class="bh-plu">PLU: ${item.plu_code || ''}</span>
            <span class="bh-loc">Shelf:${shelfPart} Bin:${binPart}</span>
          </div>
          ${item.barcode ? `<div class="bh-bcnum">${item.barcode}</div>` : ''}
          <div class="bh-name">${item.name || ''}</div>
        </div>
      `;
    });
    // Safe printable area = 35mm × 27mm (2.5mm horiz × 1.5mm vert dead margin).
    // See stockLabelPrint.js for why we widened the safe area from 1mm to 2.5mm.
    const padTop = 1.5;
    const padRight = 2.5;
    const padBottom = 1.5;
    const padLeft = 2.5;
    const shiftX = companyInfo.offsetX || 0;
    const shiftY = companyInfo.offsetY || 0;
    return `<!doctype html><html><head><meta charset="utf-8" /><title>Shelf Talkers</title>
<style>
@page { size: 40mm 30mm; margin: 0; }
html, body { background: #fff; margin: 0; padding: 0; font-family: Arial, sans-serif; color: #000; }
.bh-label {
  width: 40mm; height: 30mm;
  padding: ${padTop}mm ${padRight}mm ${padBottom}mm ${padLeft}mm;
  transform: translate(${shiftX}mm, ${shiftY}mm); transform-origin: top left;
  page-break-after: always;
  display: flex; flex-direction: column;
  box-sizing: border-box; overflow: hidden;
}
.bh-label:last-child { page-break-after: auto; }
.bh-row1 { display: flex; justify-content: space-between; align-items: center; }
.bh-logo { font-size: 9pt; font-weight: 900; background: #000; color: #fff; padding: 0.3mm 1.5mm; letter-spacing: 0.3mm; }
.bh-price { font-size: 13pt; font-weight: 900; color: #000; white-space: nowrap; }
.bh-row2 { display: flex; justify-content: space-between; align-items: center; font-size: 7pt; font-weight: bold; margin-top: 0.2mm; }
.bh-bcnum { font-size: 7pt; font-family: monospace; text-align: center; letter-spacing: 0.3mm; margin-top: 0.3mm; }
.bh-name { font-size: 9pt; text-align: center; font-weight: bold; line-height: 1.1; text-transform: uppercase; margin-top: auto; overflow: hidden; max-height: 12.5mm;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; word-break: break-word; }
* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
</style></head><body>${labelsHtml}</body></html>`;
  };

  const handleSendToStorePrinter = async () => {
    const html = buildShelfTalkerHtml();
    if (!html) { toast.warning("Select at least one item first."); return; }
    try {
      await submitPrintJob({
        jobType: "shelf_talkers",
        title: `Shelf Talkers — ${selectedItems.length} item${selectedItems.length !== 1 ? "s" : ""}`,
        html,
        pageSizeHint: "40mm 30mm",
        sourceType: "manual",
      });
      toast.success(`${selectedItems.length} shelf talker${selectedItems.length !== 1 ? "s" : ""} queued for the store printer.`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue print job");
    }
  };

  const handlePrint = () => {
    setPrinting(true);

    const selectedData = items.filter(item => selectedItems.includes(item.product_id));

    // Build labels HTML
    let labelsHtml = '';
    selectedData.forEach(item => {
      const shelfPart = item.location?.split('/')[0] || item.shelf_number || '-';
      const binPart = item.location?.split('/')[1] || item.bin_number || '-';
      const price = priceFor(item);

      labelsHtml += `
        <div class="bh-label">
          <div class="bh-row1">
            <div class="bh-logo">${companyInfo.shortName || 'BBH'}</div>
            <div class="bh-price">R${price.toFixed(2)}</div>
          </div>
          <div class="bh-row2">
            <span class="bh-plu">PLU: ${item.plu_code || ''}</span>
            <span class="bh-loc">Shelf:${shelfPart} Bin:${binPart}</span>
          </div>
          ${item.barcode ? `<div class="bh-bcnum">${item.barcode}</div>` : ''}
          <div class="bh-name">${item.name || ''}</div>
        </div>
      `;
    });

    // Print directly from the main window using @media print to swap content.
    // This is the only print path that works reliably in Edge PWA / standalone-app mode
    // (popups and iframes both trigger the native "Print Failed" dialog in PWA mode).
    const PRINT_HOST_ID = 'bh-shelf-talker-print-host';
    const PRINT_STYLE_ID = 'bh-shelf-talker-print-style';

    // Remove previous host/style if they exist (from a prior print)
    document.getElementById(PRINT_HOST_ID)?.remove();
    document.getElementById(PRINT_STYLE_ID)?.remove();

    const host = document.createElement('div');
    host.id = PRINT_HOST_ID;
    host.innerHTML = labelsHtml;
    document.body.appendChild(host);

    const style = document.createElement('style');
    style.id = PRINT_STYLE_ID;
    style.textContent = `
      /* Hide host on screen, only show during print */
      #${PRINT_HOST_ID} { display: none; }

      @media print {
        @page { size: 40mm 30mm; margin: 0; }

        /* Belt-and-braces isolation: hide everything in the document by default,
           then explicitly show only our print host. This survives:
            - Radix/Portal children that don't sit as direct body children
            - Any inline styles or !important rules from third-party libs
            - Position:fixed elements that paint on top of every printed page */
        html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
        body * { visibility: hidden !important; }
        body > *:not(#${PRINT_HOST_ID}) { display: none !important; }
        #emergent-badge,
        [id="emergent-badge"],
        [class*="emergent-badge"] { display: none !important; visibility: hidden !important; }

        /* Promote the host above any leftover stacking context and force it visible.
           NOTE: do NOT use position:fixed here - fixed elements are removed from
           normal flow and 'page-break-after' on each label is ignored, which makes
           multi-label prints collapse onto a single page. */
        #${PRINT_HOST_ID},
        #${PRINT_HOST_ID} * { visibility: visible !important; }
        #${PRINT_HOST_ID} {
          display: block !important;
          position: static !important;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
        }

        #${PRINT_HOST_ID} .bh-label {
          width: 40mm; height: 30mm;
          /* Wider safe padding (2.5mm/1.5mm — was 0.5mm/1mm). See buildShelfTalkerHtml. */
          padding: 1.5mm 2.5mm 1.5mm 2.5mm;
          transform: translate(${(companyInfo.offsetX || 0)}mm, ${(companyInfo.offsetY || 0)}mm);
          transform-origin: top left;
          page-break-after: always;
          display: flex; flex-direction: column;
          box-sizing: border-box;
          font-family: Arial, sans-serif;
          color: #000;
          overflow: hidden;
        }
        #${PRINT_HOST_ID} .bh-label:last-child { page-break-after: auto; }
        #${PRINT_HOST_ID} .bh-row1 { display: flex; justify-content: space-between; align-items: center; }
        #${PRINT_HOST_ID} .bh-logo { font-size: 9pt; font-weight: 900; background: #000; color: #fff; padding: 0.3mm 1.5mm; letter-spacing: 0.3mm; }
        #${PRINT_HOST_ID} .bh-price { font-size: 13pt; font-weight: 900; color: #000; white-space: nowrap; }
        #${PRINT_HOST_ID} .bh-row2 { display: flex; justify-content: space-between; align-items: center; font-size: 7pt; font-weight: bold; margin-top: 0.2mm; }
        #${PRINT_HOST_ID} .bh-plu { font-weight: bold; }
        #${PRINT_HOST_ID} .bh-bcnum { font-size: 7pt; font-family: monospace; text-align: center; letter-spacing: 0.3mm; margin-top: 0.3mm; }
        #${PRINT_HOST_ID} .bh-loc { font-weight: bold; font-size: 7pt; }
        #${PRINT_HOST_ID} .bh-name { font-size: 9pt; text-align: center; font-weight: bold; line-height: 1.1; text-transform: uppercase; margin-top: auto; overflow: hidden; max-height: 12.5mm;
          display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; }

        /* Make sure background colours print on the price logo */
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `;
    document.head.appendChild(style);

    const cleanup = () => {
      try { host.remove(); } catch (_) {}
      try { style.remove(); } catch (_) {}
      // Restore the Emergent badge (and any other elements we hid)
      try {
        const badge = document.getElementById('emergent-badge');
        if (badge && badge.dataset.bhPrintOldDisplay !== undefined) {
          badge.style.display = badge.dataset.bhPrintOldDisplay;
          delete badge.dataset.bhPrintOldDisplay;
        }
      } catch (_) {}
      window.removeEventListener('afterprint', cleanup);
      setPrinting(false);
    };
    window.addEventListener('afterprint', cleanup);

    // Force-hide the Emergent badge — its inline `style="display: flex !important"`
    // beats any external CSS rule, so we have to set it directly on the element.
    try {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.dataset.bhPrintOldDisplay = badge.style.display || '';
        badge.style.display = 'none';
      }
    } catch (_) {}

    // Wait for barcode images to fully decode, then print
    const imgs = Array.from(host.querySelectorAll('img'));
    const startPrint = () => {
      try {
        window.print();
      } catch (e) {
        console.error('window.print() error:', e);
        toast.error('Print failed. Please try again.');
        cleanup();
        return;
      }
      // Some browsers don't fire afterprint reliably — clean up after a delay too
      setTimeout(cleanup, 4000);
    };

    if (imgs.length === 0) {
      setTimeout(startPrint, 50);
      return;
    }

    let pending = imgs.length;
    let started = false;
    const checkDone = () => {
      pending -= 1;
      if (pending <= 0 && !started) { started = true; setTimeout(startPrint, 50); }
    };
    imgs.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) checkDone();
      else { img.addEventListener('load', checkDone); img.addEventListener('error', checkDone); }
    });
    // Safety net — never wait longer than 3s
    setTimeout(() => { if (!started) { started = true; startPrint(); } }, 3000);
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const selectedData = items.filter(item => selectedItems.includes(item.product_id));
      const payload = {
        company_short_name: companyInfo.shortName || 'BBH',
        items: selectedData.map(item => ({
          name: item.name || '',
          plu_code: item.plu_code || '',
          barcode: item.barcode || item.plu_code || '',
          // Send the freshly-fetched DB price as `new_selling_price` so the
          // backend PDF renderer picks it up (it prefers `new_selling_price`
          // over `selling_price`). This keeps the printed label identical to
          // the Product Master value even if the form-time snapshot was stale.
          selling_price: item.selling_price || 0,
          new_selling_price: priceFor(item),
          location: item.location || '',
          shelf_number: item.shelf_number || '',
          bin_number: item.bin_number || '',
        })),
      };

      const response = await axios.post(`${API}/labels/shelf-talkers-pdf`, payload, {
        responseType: 'blob',
      });

      // Trigger browser download
      const blobUrl = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `shelf-talkers-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success('PDF downloaded — open it and print from your PDF viewer.');
    } catch (e) {
      console.error('PDF download error:', e);
      toast.error('Could not generate the PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const selectedItemsData = items.filter(item => selectedItems.includes(item.product_id));
  const filteredIds = filteredItems.map(item => item.product_id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selectedItems.includes(id));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-blue-600" />
            Print Shelf Talkers
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {/* Search Input */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by product name, PLU, or barcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Selection controls */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={allFilteredSelected}
                onCheckedChange={toggleAll}
              />
              <span className="text-sm font-medium">
                {searchQuery ? `Select Filtered (${filteredItems.length})` : 'Select All'} 
                <span className="text-gray-500 ml-1">({selectedItems.length} selected)</span>
              </span>
            </div>
            <div className="text-sm text-gray-500">
              40mm × 30mm shelf talkers
            </div>
          </div>

          {/* Items list */}
          <div className="space-y-2 mb-4 max-h-[250px] overflow-auto">
            {filteredItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No products found matching &quot;{searchQuery}&quot;
              </div>
            ) : (
              filteredItems.map(item => (
                <div 
                  key={item.product_id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedItems.includes(item.product_id) ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleItem(item.product_id)}
                >
                  <Checkbox 
                    checked={selectedItems.includes(item.product_id)}
                    onCheckedChange={() => toggleItem(item.product_id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.name}</div>
                    <div className="text-sm text-gray-500">
                      PLU: {item.plu_code} | Barcode: {item.barcode || 'N/A'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">
                      R {priceFor(item).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Preview - Matching StockLabelPrinting.jsx design */}
          {selectedItemsData.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium mb-3">Preview (40mm × 30mm)</h3>
              <div className="flex flex-wrap gap-4 justify-center bg-gray-100 p-4 rounded-lg">
                {selectedItemsData.slice(0, 2).map(item => {
                  const shelfPart = item.location?.split('/')[0] || item.shelf_number || '-';
                  const binPart = item.location?.split('/')[1] || item.bin_number || '-';
                  const price = priceFor(item);
                  return (
                  <div 
                    key={item.product_id}
                    className="bg-white border-2 border-gray-400"
                    style={{ width: '151px', height: '113px', padding: '3px 5px', fontFamily: 'Arial', display: 'flex', flexDirection: 'column' }}
                  >
                    {/* Row 1: Logo and Price */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: '900', 
                        background: '#000', 
                        color: '#fff', 
                        padding: '2px 8px',
                        letterSpacing: '1px',
                      }}>
                        {companyInfo.shortName || 'BBH'}
                      </span>
                      <span style={{ fontSize: '18px', fontWeight: '900' }}>R{price.toFixed(2)}</span>
                    </div>
                    {/* Row 2: PLU and Location */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', fontWeight: 'bold', marginBottom: '2px' }}>
                      <span>PLU: {item.plu_code}</span>
                      <span style={{ fontSize: '10px' }}>Shelf:{shelfPart} Bin:{binPart}</span>
                    </div>
                    {item.barcode && <div style={{ fontSize: '10px', fontFamily: 'monospace', textAlign: 'center', letterSpacing: '1px' }} data-testid={`shelf-talker-barcode-num-${item.plu_code}`}>{item.barcode}</div>}
                    {/* Product name */}
                    <div style={{ fontSize: '12px', fontWeight: 'bold', textAlign: 'center', textTransform: 'uppercase', marginTop: 'auto', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                      {item.name}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={selectedItems.length === 0 || downloadingPdf || printing}
            className="border-blue-600 text-blue-700 hover:bg-blue-50"
            data-testid="download-shelf-talkers-pdf-btn"
            title="Bulletproof fallback when 'Print Failed' appears — opens a normal PDF you print from any viewer."
          >
            <Download className="h-4 w-4 mr-2" />
            {downloadingPdf ? 'Generating…' : 'Download as PDF'}
          </Button>
          <Button
            variant="outline"
            onClick={handleSendToStorePrinter}
            disabled={selectedItems.length === 0 || printing}
            className="border-purple-600 text-purple-700 hover:bg-purple-50"
            data-testid="send-shelf-talkers-to-store-btn"
            title="Queue to the store's printer (use this when you're working from home)"
          >
            <Send className="h-4 w-4 mr-2" />
            Send to Store Printer
          </Button>
          <Button
            onClick={handlePrint}
            disabled={selectedItems.length === 0 || printing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Printer className="h-4 w-4 mr-2" />
            {printing ? 'Printing...' : `Print ${selectedItems.length} Shelf Talker${selectedItems.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShelfTalkerModal;
